import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { Button, Form, FormGroup, Input, Label, Spinner, FormFeedback, Alert } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';

import { media } from '../data/cloudinary';
import { routeUrls } from '../config/routes';
import { login } from '../redux/actions';

import { MdVisibility, MdVisibilityOff } from 'react-icons/md';

const LoginForm = ({ showLogo, onLogoClick }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { loader, error } = useSelector(state => ({
    loader: state.loader,
    error: state.error
  }));

  const [browniteInfo, setBrowniteInfo] = useState({
    matricNumber: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const validateField = (name, value) => {
    if (name === 'matricNumber') {
      if (!value) return 'Matric number is required';
      if (value.toString().length !== 6) return 'Matric number must be 6 digits';
      return null;
    }
    if (name === 'password') {
      if (!value) return 'Password is required';
      if (value.length < 6) return 'Password must be at least 6 characters';
      return null;
    }
    return null;
  };

  const handleInput = e => {
    const { name, value } = e.target;
    setBrowniteInfo(prevState => ({
      ...prevState,
      [name]: value,
    }));

    // Real-time validation after field is touched
    if (touched[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: validateField(name, value),
      }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    setErrors(prev => ({
      ...prev,
      [name]: validateField(name, value),
    }));
  };

  const isFormValid = () => {
    return (
      browniteInfo.matricNumber.toString().length === 6 &&
      browniteInfo.password.length >= 6
    );
  };

  const handleSubmit = event => {
    event.preventDefault();

    // Mark all fields as touched
    setTouched({ matricNumber: true, password: true });

    // Validate all fields
    const newErrors = {
      matricNumber: validateField('matricNumber', browniteInfo.matricNumber),
      password: validateField('password', browniteInfo.password),
    };
    setErrors(newErrors);

    // Only submit if no errors
    if (!newErrors.matricNumber && !newErrors.password) {
      dispatch(login(browniteInfo));
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      {showLogo && (
        <div className="text-center pb-4">
          <img
            src={media.logo}
            className="rounded"
            style={{ width: 60, height: 60, cursor: 'pointer' }}
            alt="logo"
            onClick={onLogoClick}
          />
        </div>
      )}

      {error && error.message && (
        <Alert color="danger">
          {error.message}
        </Alert>
      )}

      <FormGroup>
        <Label for="matricNumber">Matric Number</Label>
        <Input
          type="number"
          id="matricNumber"
          value={browniteInfo.matricNumber}
          onChange={handleInput}
          onBlur={handleBlur}
          name="matricNumber"
          placeholder="Your 6-digit matric number"
          invalid={touched.matricNumber && !!errors.matricNumber}
          valid={touched.matricNumber && !errors.matricNumber && browniteInfo.matricNumber.length > 0}
        />
        {touched.matricNumber && errors.matricNumber && (
          <FormFeedback>{errors.matricNumber}</FormFeedback>
        )}
      </FormGroup>
      <FormGroup>
        <Label for="password">Password</Label>
        <div className="input-group">
          <Input
            type={showPassword ? "text" : "password"}
            id="password"
            name="password"
            value={browniteInfo.password}
            onChange={handleInput}
            onBlur={handleBlur}
            placeholder="Your password (min. 6 characters)"
            invalid={touched.password && !!errors.password}
            valid={touched.password && !errors.password && browniteInfo.password.length > 0}
            className="form-control"
          />
          <div className="input-group-append">
            <span
              className="input-group-text"
              onClick={() => setShowPassword(!showPassword)}
              style={{ cursor: 'pointer' }}
            >
              {showPassword ? <MdVisibilityOff /> : <MdVisibility />}
            </span>
          </div>
          {touched.password && errors.password && (
            <FormFeedback style={{ display: 'block' }}>{errors.password}</FormFeedback>
          )}
        </div>
      </FormGroup>

      <FormGroup check>
        <Label check>
          <Input type="checkbox" /> Remember me
        </Label>
      </FormGroup>
      <hr />
      <FormGroup style={{ display: 'flex', justifyContent: 'center' }}>
        <Button
          size="lg"
          className="bg-gradient-theme-left border-0"
          block
          onClick={handleSubmit}
          disabled={loader && loader.login}
        >
          Login {loader && loader.login && <Spinner color="light" size="sm" />}
        </Button>
      </FormGroup>

      <div className="text-center pt-1">
        <h6>
          <p
            onClick={() => history.push(routeUrls.forgotPassword)}
            style={{ cursor: 'pointer', color: '#13287D', marginBottom: '0.5rem' }}
          >
            Forgot Password?
          </p>
        </h6>
        <h6>or</h6>
        <h6>
          <p
            onClick={() => history.push(routeUrls.signup)}
            style={{ cursor: 'pointer' }}
          >
            Signup
          </p>
        </h6>
      </div>
    </Form>
  );
};

LoginForm.propTypes = {
  showLogo: PropTypes.bool,
  onLogoClick: PropTypes.func,
};

LoginForm.defaultProps = {
  showLogo: true,
  onLogoClick: () => { },
};
export default LoginForm;
